import { Box } from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import { openUrl } from '@tauri-apps/plugin-opener';
import { useHref, useNavigate } from 'react-router-dom';

interface NavigationLinkProps {
    admin?: boolean;
    children?: React.ReactNode;
    external?: boolean;
    fullWidth?: boolean;
    openInNewTab?: boolean;
    to: string | undefined;
}

export default function NavigationLink({
    admin,
    children,
    external,
    fullWidth,
    openInNewTab,
    to,
}: Readonly<NavigationLinkProps>) {
    const navigate = useNavigate();
    const internalHref = useHref(to ?? '/');
    const isTauriApp = isTauri();
    const isAbsoluteUrl = to ? /^(https?:\/\/|www\.|mailto:|tel:)/.test(to) : false;
    const resolvedHref = !to ? '#' : isAbsoluteUrl || admin || external ? to : internalHref;

    const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (!to) {
            return;
        }

        const isExternalLink = isAbsoluteUrl;
        const isPdfLink = to.endsWith('.pdf');
        const isEmailLink = to.startsWith('mailto:');

        // Handle Tauri
        if (isTauriApp) {
            if (isPdfLink || isEmailLink) {
                try {
                    await openUrl(to);
                    return;
                } catch (error) {
                    console.error('Error opening URL in Tauri:', error);
                    // fallback to default behavior
                    window.location.href = to;
                    return;
                }
            }
            if (external) {
                await openUrl(to);
                return;
            }
            if (isExternalLink || admin) {
                window.location.href = to;
                return;
            } else {
                navigate(to);
                return;
            }
        }

        // Handle browser, testing how it feels if not opening external links in a new tab
        if (openInNewTab) {
            window.open(resolvedHref, '_blank');
        } else if (isExternalLink || admin) {
            window.open(resolvedHref, '_self');
        } else {
            navigate(to);
        }
    };

    return (
        <Box
            component="a"
            href={resolvedHref}
            onClick={handleClick}
            sx={{
                color: 'inherit',
                cursor: to ? 'pointer' : 'default',
                textDecoration: 'none',
                width: fullWidth ? '100%' : 'auto',
            }}
        >
            {children}
        </Box>
    );
}
